import type {
  EventPayload,
  EventType,
  PixelConfig,
  SessionIds,
} from "./types.js";
import type { Transport } from "./transport.js";

const PII_KEY_RE = /(email|phone|ssn|password|passwd|secret|card|cvv|cvc|token|api[_-]?key|auth)/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9\s\-().]{6,}$/;
const MAX_METADATA_BYTES = 4096;

function sanitizeValue(v: unknown, depth: number): unknown {
  if (typeof v === "string") {
    const s = v.trim();
    if (EMAIL_RE.test(s)) return undefined;
    if (PHONE_RE.test(s)) return undefined;
    return v;
  }
  if (v && typeof v === "object" && depth > 0 && !Array.isArray(v)) {
    return sanitizeObject(v as Record<string, unknown>, depth - 1);
  }
  return v;
}

function sanitizeObject(
  obj: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj)) {
    if (PII_KEY_RE.test(k)) continue;
    const cleaned = sanitizeValue(obj[k], depth);
    if (cleaned === undefined) continue;
    out[k] = cleaned;
  }
  return out;
}

function sanitizeMetadata(
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!metadata) return {};
  const cleaned = sanitizeObject(metadata, 1);
  let serialized = JSON.stringify(cleaned);
  if (serialized.length <= MAX_METADATA_BYTES) return cleaned;
  // Drop largest keys until under cap.
  const entries = Object.entries(cleaned).sort(
    (a, b) => JSON.stringify(b[1]).length - JSON.stringify(a[1]).length,
  );
  const trimmed: Record<string, unknown> = {};
  for (const [k, v] of entries) trimmed[k] = v;
  while (serialized.length > MAX_METADATA_BYTES) {
    const keys = Object.keys(trimmed);
    if (keys.length === 0) break;
    let largestKey = keys[0]!;
    let largestSize = JSON.stringify(trimmed[largestKey]).length;
    for (const k of keys) {
      const size = JSON.stringify(trimmed[k]).length;
      if (size > largestSize) {
        largestSize = size;
        largestKey = k;
      }
    }
    delete trimmed[largestKey];
    serialized = JSON.stringify(trimmed);
  }
  return trimmed;
}

export function exposeApi(
  cfg: PixelConfig,
  ids: SessionIds,
  getFp: () => string | undefined,
  transport: Transport,
): void {
  if (typeof window === "undefined") return;
  window.intent = {
    track(eventType: EventType, metadata?: Record<string, unknown>): void {
      try {
        const fp = getFp();
        const payload: EventPayload = {
          pixel_id: cfg.pixelId,
          session_id: ids.sessionId,
          visitor_id: ids.visitorId,
          event_type: eventType,
          page_url: typeof location !== "undefined" ? location.href : "",
          metadata: sanitizeMetadata(metadata),
          occurred_at: new Date().toISOString(),
        };
        if (fp) payload.device_fp = fp;
        transport.send(payload);
        ids.touch();
      } catch {
        // never throw out of pixel API
      }
    },
  };
}
